import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import { useNavigate } from 'react-router-dom'
import Modal from 'components/atoms/Modal'
import APIService from 'services/APIService'
import { PRIVATE_PATHS } from 'utils/paths'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'

const formatDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const PER_PAGE = 50

const FileTypeFilesDialog = ({ portfolioId, fileType, open, onClose }) => {
  const navigate = useNavigate()
  const [files, setFiles] = useState([])
  const [meta, setMeta] = useState(null) // { currentPage, lastPage, total, perPage }
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [insightFile, setInsightFile] = useState(null)

  // Initial load when the dialog opens
  useEffect(() => {
    if (!open || !portfolioId || !fileType?.fileTypeId) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      setFiles([])
      setMeta(null)
      try {
        const api = APIService()
        const res = await api.get(
          `/portfolios/${portfolioId}/files`,
          { params: { 'filter[type]': fileType.fileTypeId, page: 1, per_page: PER_PAGE } }
        )
        if (!cancelled) {
          setFiles(res?.data?.data || [])
          setMeta(res?.data?.meta || null)
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load files for this type.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [open, portfolioId, fileType?.fileTypeId])

  const loadMore = async () => {
    if (!meta || meta.currentPage >= meta.lastPage || loadingMore) return
    setLoadingMore(true)
    try {
      const api = APIService()
      const nextPage = meta.currentPage + 1
      const res = await api.get(
        `/portfolios/${portfolioId}/files`,
        { params: { 'filter[type]': fileType.fileTypeId, page: nextPage, per_page: PER_PAGE } }
      )
      const newRows = res?.data?.data || []
      setFiles(prev => [...prev, ...newRows])
      setMeta(res?.data?.meta || meta)
    } catch (e) {
      // Keep what we have; don't block.
    } finally {
      setLoadingMore(false)
    }
  }

  const hasMore = meta && meta.currentPage < meta.lastPage

  if (!fileType) return null

  const goToCalendar = () => {
    navigate(`${PRIVATE_PATHS.calendar}?file_type_ids=${fileType.fileTypeId}`)
    onClose?.()
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={null}
        maxWidth="sm"
        fullWidth
      >
        <Container>
          <Header>
            <TypeTitle>{fileType.name}</TypeTitle>
            <TypeMeta>
              {(meta?.total ?? fileType.count ?? files.length)} document{(meta?.total ?? fileType.count ?? files.length) === 1 ? '' : 's'} across the portfolio
              {meta && meta.total > files.length && (
                <> · showing {files.length}</>
              )}
            </TypeMeta>
          </Header>

          {loading ? (
            <LoadingRow>
              <CircularProgress size={20} sx={{ color: '#D24724' }} />
              <span>Loading documents…</span>
            </LoadingRow>
          ) : error ? (
            <EmptyRow>{error}</EmptyRow>
          ) : files.length === 0 ? (
            <EmptyRow>No documents of this type found.</EmptyRow>
          ) : (
            <FileList>
              {files.map(f => (
                <FileRow
                  key={f.id}
                  onClick={() => setInsightFile({
                    id: f.id,
                    displayName: f.displayName || f.name,
                    buildingPassportId: f.buildingPassport?.id,
                    buildingPassportName: f.buildingPassport?.name,
                  })}
                >
                  <FileIconWrap>
                    <DescriptionOutlinedIcon style={{ fontSize: 20 }} />
                  </FileIconWrap>
                  <FileInfo>
                    <FileName>{f.displayName || f.name}</FileName>
                    <FileMeta>
                      {f.buildingPassport?.name && (
                        <MetaChip>
                          <ApartmentOutlinedIcon style={{ fontSize: 12 }} />
                          {f.buildingPassport.name}
                        </MetaChip>
                      )}
                      {f.expiresAt && (
                        <span>Expires {formatDate(f.expiresAt)}</span>
                      )}
                    </FileMeta>
                  </FileInfo>
                  <OpenInNewIcon style={{ fontSize: 14, opacity: 0.4 }} />
                </FileRow>
              ))}
              {hasMore && (
                <LoadMoreRow>
                  <LoadMoreButton type="button" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore
                      ? <><CircularProgress size={14} sx={{ color: '#475569' }} /> Loading more…</>
                      : <>Load more ({meta.total - files.length} remaining)</>}
                  </LoadMoreButton>
                </LoadMoreRow>
              )}
            </FileList>
          )}

          <Footer>
            <FooterLink type="button" onClick={goToCalendar}>
              View on calendar →
            </FooterLink>
          </Footer>
        </Container>
      </Modal>

      {insightFile && insightFile.buildingPassportId && (
        <GuruInsightModal
          open
          onClose={() => setInsightFile(null)}
          file={insightFile}
          bpId={insightFile.buildingPassportId}
          buildingPassportName={insightFile.buildingPassportName}
        />
      )}
    </>
  )
}

export default FileTypeFilesDialog

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0 0;
`

const Header = styled.div`
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
`

const TypeTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
`

const TypeMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
`

const FileList = styled.div`
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  &:last-child { border-bottom: none; }
  &:hover { background: #f9fafb; }
`

const FileIconWrap = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const FileName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FileMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
`

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
`

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 12px;
  font-size: 13px;
  color: #64748b;
`

const EmptyRow = styled.div`
  padding: 32px 12px;
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
`

const LoadMoreRow = styled.div`
  padding: 10px 12px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: center;
`

const LoadMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  &:hover { background: #f8fafc; }
  &:disabled { opacity: 0.6; cursor: default; }
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 6px;
`

const FooterLink = styled.button`
  background: transparent;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: #D24724;
  cursor: pointer;
  padding: 4px 6px;
  &:hover { text-decoration: underline; }
`
